/* eslint-disable react/prop-types */
import { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  TextField,
  Chip,
  Checkbox,
  CircularProgress,
  Link,
} from "@mui/material";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { protectedResources } from "authConfig";

const API_BASE = protectedResources.apiTodoList.endpoint;

const confBg = (c) => {
  if (c >= 70) return "#e8f5e9";
  if (c >= 40) return "#fff3e0";
  return "#ffebee";
};
const confColor = (c) => {
  if (c >= 70) return "#1b5e20";
  if (c >= 40) return "#e65100";
  return "#b71c1c";
};
const FEEDBACK_LABELS = { good: "👍 Good", indifferent: "😐 OK", bad: "👎 Bad" };

/**
 * ID96 — AI "Find the best available signposts" dialog.
 * Searches the internal library first, offers a web-source search fallback, lets the user
 * attach internal suggestions to the draft risk or request a new external source, and
 * captures good/bad/indifferent feedback.
 */
export default function SignpostFinderDialog({
  open,
  onClose,
  riskId,
  execute,
  onAttached,
  onCreated,
}) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null); // { reply, suggestions, noneFound, offerWeb, mode }
  const [selected, setSelected] = useState({}); // libraryId -> suggestion
  const [feedbackDone, setFeedbackDone] = useState(false);
  const [requestingUrl, setRequestingUrl] = useState(null); // web source request in progress
  const [error, setError] = useState("");
  const [requested, setRequested] = useState(null); // { caseNumber, name } — confirmation after a request
  // AI "suggest new signposts" (design agent) — folded into this one modal so it covers
  // Library (default) → AI suggest → Web, instead of a separate "Suggest signposts" dialog.
  const [aiMode, setAiMode] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiData, setAiData] = useState(null); // { summary, quantitative[], stats } | { error:true }
  const [pickQuant, setPickQuant] = useState({}); // index -> boolean (which proposals to create)
  const [creating, setCreating] = useState(false);
  // ID201 — review step: after "Review & set up" the picked proposals are copied here with their
  // suggested thresholds (editable); nothing is written until the user accepts responsibility.
  const [reviewMode, setReviewMode] = useState(false);
  const [reviewList, setReviewList] = useState([]);
  const [accepted, setAccepted] = useState(false);
  const [reviewSource, setReviewSource] = useState("ai"); // 'ai' (create-signposts) | 'library' (/signposts)
  const startedRef = useRef(false);
  const inFlightRef = useRef(false); // synchronous guard against double-submit

  const runFind = async (mode, userText) => {
    setLoading(true);
    setFeedbackDone(false);
    setSelected({});
    const nextMessages = userText ? [...messages, { role: "user", content: userText }] : messages;
    setMessages(nextMessages);
    try {
      const res = await execute("POST", `${API_BASE}/emergingRisk/${riskId}/signpost-finder`, {
        mode,
        messages: nextMessages,
      });
      const data = res?.data || res || {};
      setResult(data);
      if (data.reply) {
        setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
      }
    } catch {
      setResult({
        reply: "The finder is unavailable right now. Please try again later.",
        suggestions: [],
      });
    } finally {
      setLoading(false);
    }
  };

  // Auto-run the internal search when opened.
  useEffect(() => {
    if (open && !startedRef.current && riskId) {
      startedRef.current = true;
      runFind("internal");
    }
    if (!open) {
      startedRef.current = false;
      setMessages([]);
      setResult(null);
      setSelected({});
      setInput("");
      setFeedbackDone(false);
      setError("");
      setAiMode(false);
      setAiData(null);
      setPickQuant({});
      setReviewMode(false);
      setReviewList([]);
      setAccepted(false);
    }
  }, [open, riskId]);

  const toggleSelect = (s) =>
    setSelected((prev) => {
      const key = s.libraryId;
      const next = { ...prev };
      if (next[key]) delete next[key];
      else next[key] = s;
      return next;
    });

  // execute() resolves (does not throw) on HTTP errors, returning { success, data, message }.
  // Treat a missing/false success flag as a failure.
  const isOk = (res) => res && res.success !== false && !(res.error && res.success === undefined);

  // ID201 — library signposts go through the SAME review-thresholds + accept-responsibility step as
  // AI-suggested ones. "Set up" / "Review & set up" opens the review seeded with a Best/Worst-case
  // threshold for the user to fill in; nothing is written until they accept responsibility.
  const startLibraryReview = (items) => {
    const list = items && items.length ? items : Object.values(selected);
    if (!list.length) {
      setError("Tick at least one signpost first.");
      return;
    }
    if (!riskId) {
      setError("Save the risk details first (click Next), then add signposts.");
      return;
    }
    setReviewList(
      list.map((s) => ({
        libraryId: s.libraryId,
        signpostLibraryId: s.libraryId,
        name: s.name,
        description: s.description || "",
        unit: s.unit || "",
        thresholds: [
          { label: "Best Case", value: "", scenario: "optimistic" },
          { label: "Worst Case", value: "", scenario: "pessimistic" },
        ],
      }))
    );
    setReviewSource("library");
    setAccepted(false);
    setError("");
    setReviewMode(true);
  };

  const requestSource = async (s) => {
    if (inFlightRef.current) return;
    if (!riskId) {
      setError("Save the risk details first (click Next), then request a source.");
      return;
    }
    inFlightRef.current = true;
    setError("");
    setRequestingUrl(s.sourceUrl || s.name);
    try {
      const res = await execute("POST", `${API_BASE}/emergingRisk/${riskId}/signpost-request`, {
        name: s.name,
        description: s.description || "",
        sourceUrl: s.sourceUrl || "",
      });
      if (!isOk(res)) {
        setError(`Couldn't submit the request. ${res?.message || "Please try again."}`);
        return;
      }
      const caseNumber = res?.data?.caseNumber || "";
      const requestId = res?.data?.id ? String(res.data.id) : "";
      // Add a PLACEHOLDER signpost to the risk now (tagged 'pending' + linked to the request), so it
      // shows as "being requested" and the approval can update THIS row instead of duplicating it.
      try {
        await execute("POST", `${API_BASE}/emergingRisk/${riskId}/signposts`, {
          name: s.name,
          subType: "quantitative",
          desc: s.description || "",
          parameters: { sourceUrl: s.sourceUrl || "" },
          thresholds: [],
          triggers: [],
          requestId,
          requestCaseNumber: caseNumber,
          requestStatus: "pending",
        });
      } catch {
        /* the request is already filed; ignore a signpost-create hiccup */
      }
      // Show the case number so the user knows it was logged (ID164). They click Done to close/refresh.
      setRequested({ caseNumber, name: s.name });
    } catch (e) {
      setError(`Couldn't submit the request. ${e?.message || "Please try again."}`);
    } finally {
      setRequestingUrl(null);
      inFlightRef.current = false;
    }
  };

  const sendFeedback = async (rating) => {
    setFeedbackDone(true);
    try {
      await execute("POST", `${API_BASE}/emergingRisk/signpost-feedback`, {
        riskId,
        source: result?.mode === "web" ? "web" : "internal",
        rating,
        query: messages.map((m) => m.content).join(" | "),
        suggestions: (result?.suggestions || []).map((s) => ({
          name: s.name,
          confidence: s.confidence,
        })),
      });
    } catch {
      /* ignore */
    }
  };

  // ── AI "suggest new signposts" (design agent) ────────────────────────────────
  // Researches real-world proxies and validates live data feeds, then proposes brand-new
  // quantitative signposts (not from the library). Only runs on an explicit click (it's slow + paid).
  const runSuggest = async () => {
    if (!riskId) {
      setError("Save the risk details first (click Next), then suggest signposts.");
      return;
    }
    setAiMode(true);
    setAiLoading(true);
    setAiData(null);
    setError("");
    try {
      const res = await execute("POST", `${API_BASE}/emergingRisk/${riskId}/suggest-signposts`, {});
      const d = res?.data || null;
      setAiData(d || { error: true });
      const pq = {};
      (d?.quantitative || []).forEach((_, i) => {
        pq[i] = true; // pre-select all proposals
      });
      setPickQuant(pq);
    } catch {
      setAiData({ error: true });
    } finally {
      setAiLoading(false);
    }
  };

  const aiPickedCount = Object.values(pickQuant).filter(Boolean).length;

  // ID201 — "Review & set up" opens a review step (no write yet): copy the picked proposals with
  // their suggested thresholds so the user can adjust them before accepting responsibility.
  const startReview = () => {
    const quantitative = (aiData?.quantitative || []).filter((_, i) => pickQuant[i]);
    if (!quantitative.length) {
      setError("Tick at least one signpost to set up.");
      return;
    }
    if (!riskId) {
      setError("Save the risk details first (click Next), then set up signposts.");
      return;
    }
    setReviewList(
      quantitative.map((q) => ({ ...q, thresholds: (q.thresholds || []).map((t) => ({ ...t })) }))
    );
    setReviewSource("ai");
    setAccepted(false);
    setError("");
    setReviewMode(true);
  };
  const updateThreshold = (i, ti, value) => {
    setReviewList((prev) =>
      prev.map((q, qi) =>
        qi !== i
          ? q
          : { ...q, thresholds: q.thresholds.map((t, k) => (k === ti ? { ...t, value } : t)) }
      )
    );
  };
  // The only place a signpost is actually written — gated on the responsibility acknowledgement.
  const confirmCreate = async () => {
    if (creating || !reviewList.length) return;
    if (!accepted) {
      setError("Please tick the box to accept responsibility, then set up the signposts.");
      return;
    }
    setCreating(true);
    setError("");
    try {
      // Library signposts: attach each to the risk (its existing library entry) with the reviewed
      // thresholds. Empty threshold rows are dropped; the rest are set so the poller can assess them.
      if (reviewSource === "library") {
        const results = await Promise.all(
          reviewList.map((q) =>
            execute("POST", `${API_BASE}/emergingRisk/${riskId}/signposts`, {
              name: q.name,
              subType: "quantitative",
              desc: q.description || "",
              signpostLibraryId: q.signpostLibraryId || q.libraryId,
              thresholds: (q.thresholds || [])
                .filter((t) => String(t.value ?? "").trim() !== "")
                .map((t) => ({
                  label: t.label || "",
                  value: t.value,
                  scenario: t.scenario || "",
                  direction: t.direction || "",
                })),
              triggers: [],
            })
          )
        );
        const failed = results.filter((r) => !isOk(r));
        if (failed.length) {
          setError(
            `Couldn't set up ${failed.length} of ${reviewList.length} signpost(s). ${
              failed[0]?.message || "Please try again."
            }`
          );
          return;
        }
        if (onAttached) onAttached();
        onClose();
        return;
      }

      const res = await execute("POST", `${API_BASE}/emergingRisk/${riskId}/create-signposts`, {
        quantitative: reviewList,
        qualitativeTriggers: [],
        qualitativeSummary: aiData?.summary || "",
      });
      if (!isOk(res)) {
        setError(`Couldn't set up the signpost(s). ${res?.message || "Please try again."}`);
        return;
      }
      if (onCreated) onCreated(reviewList);
      else if (onAttached) onAttached();
      onClose();
    } catch (e) {
      setError(`Couldn't set up the signpost(s). ${e?.message || "Please try again."}`);
    } finally {
      setCreating(false);
    }
  };

  const suggestions = result?.suggestions || [];
  const isWeb = result?.mode === "web";

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ color: "#52009b", fontWeight: 700 }}>Add signposts</DialogTitle>
      <DialogContent dividers>
        {requested && (
          <Box sx={{ py: 2, textAlign: "center" }}>
            <MDTypography variant="h6" sx={{ color: "#2e7d32", fontWeight: 700, mb: 1 }}>
              Request logged ✓
            </MDTypography>
            <MDTypography variant="body2" sx={{ mb: 0.5 }}>
              &quot;{requested.name}&quot; has been requested as a new signpost source.
            </MDTypography>
            <MDTypography variant="body2" sx={{ mb: 1.5 }}>
              {requested.caseNumber ? (
                <>
                  Your case number is <strong>{requested.caseNumber}</strong>.{" "}
                </>
              ) : null}
              It now shows on the risk as <strong>&quot;Requested — not yet added&quot;</strong>{" "}
              until our team wires up the source.
            </MDTypography>
            <MDButton
              variant="gradient"
              color="info"
              onClick={() => {
                setRequested(null);
                if (onAttached) onAttached();
                else onClose();
              }}
              sx={{ textTransform: "none" }}
            >
              Done
            </MDButton>
          </Box>
        )}
        {!requested && (aiMode || reviewMode) && (
          <>
            {aiLoading && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size={18} sx={{ color: "#52009b" }} />
                <MDTypography variant="caption" color="text.secondary">
                  Researching real proxies and validating live data feeds… (~15–25s)
                </MDTypography>
              </Box>
            )}
            {!aiLoading && aiData?.error && (
              <MDTypography variant="body2" color="error">
                Couldn&apos;t generate suggestions. Please try again later.
              </MDTypography>
            )}
            {!aiLoading && aiData && !aiData.error && !reviewMode && (
              <Box>
                {aiData.summary && (
                  <MDTypography variant="body2" sx={{ mb: 2, fontStyle: "italic", color: "#555" }}>
                    {aiData.summary}
                  </MDTypography>
                )}
                <MDTypography variant="h6" sx={{ color: "#52009b", mb: 1 }}>
                  Suggested signposts ({(aiData.quantitative || []).length})
                </MDTypography>
                {(aiData.quantitative || []).map((q, i) => (
                  <Box
                    key={q.name || i}
                    display="flex"
                    alignItems="flex-start"
                    gap={1}
                    sx={{ mb: 1.5 }}
                  >
                    <Checkbox
                      checked={!!pickQuant[i]}
                      onChange={(e) => setPickQuant((p) => ({ ...p, [i]: e.target.checked }))}
                      sx={{
                        p: 0,
                        mt: 0.3,
                        color: "#52009b",
                        "&.Mui-checked": { color: "#52009b" },
                      }}
                    />
                    <Box sx={{ minWidth: 0 }}>
                      <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                        <MDTypography variant="button" fontWeight="bold">
                          {q.name}
                        </MDTypography>
                        <Chip
                          size="small"
                          label={q.feed_validated ? "✓ live feed" : "unvalidated"}
                          sx={{
                            bgcolor: q.feed_validated ? "#e8f5e9" : "#eeeeee",
                            color: q.feed_validated ? "#2e7d32" : "#777",
                            fontSize: 11,
                          }}
                        />
                        {q.dataSourceType && (
                          <Chip
                            size="small"
                            variant="outlined"
                            label={`${q.dataSourceType} · ${
                              q.direction === "lower_is_worse" ? "lower=worse" : "higher=worse"
                            }${q.unit ? ` · ${q.unit}` : ""}`}
                            sx={{ fontSize: 11 }}
                          />
                        )}
                      </Box>
                      {q.rationale && (
                        <MDTypography variant="caption" sx={{ display: "block", color: "#555" }}>
                          {q.rationale}
                        </MDTypography>
                      )}
                    </Box>
                  </Box>
                ))}
                {aiData.stats && (
                  <MDTypography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", mt: 2 }}
                  >
                    {aiData.stats.searches} searches · {aiData.stats.tickerTests} feeds tested · ~$
                    {Number(aiData.stats.costUsd || 0).toFixed(3)}
                  </MDTypography>
                )}
              </Box>
            )}
            {/* ID201 — review step: adjust suggested thresholds, then accept responsibility to set up. */}
            {!aiLoading && reviewMode && (
              <Box>
                <MDTypography variant="h6" sx={{ color: "#52009b", mb: 0.5 }}>
                  Review &amp; set up ({reviewList.length})
                </MDTypography>
                <MDTypography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mb: 1.5 }}
                >
                  Check each signpost and adjust the suggested threshold values if needed, then
                  accept responsibility and set them up. You can change these any time afterwards.
                </MDTypography>
                {reviewList.map((q, i) => (
                  <Box
                    key={q.name || i}
                    sx={{
                      border: "1px solid rgba(82,0,155,0.15)",
                      borderRadius: 1,
                      p: 1.5,
                      mb: 1.5,
                    }}
                  >
                    <MDTypography variant="button" fontWeight="bold" sx={{ display: "block" }}>
                      {q.name}
                      {q.unit ? (
                        <MDTypography
                          component="span"
                          variant="caption"
                          sx={{ color: "text.secondary" }}
                        >
                          {" "}
                          ({q.unit})
                        </MDTypography>
                      ) : null}
                    </MDTypography>
                    {(q.thresholds || []).length === 0 ? (
                      <MDTypography variant="caption" color="text.secondary">
                        No thresholds suggested — you can add them later.
                      </MDTypography>
                    ) : (
                      (q.thresholds || []).map((t, ti) => (
                        <Box
                          key={t.label || t.scenario || ti}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            mt: 0.75,
                            flexWrap: "wrap",
                          }}
                        >
                          <MDTypography variant="caption" sx={{ minWidth: 150 }}>
                            {t.label || t.scenario || `Threshold ${ti + 1}`}
                            {t.scenario ? ` (${t.scenario})` : ""}
                          </MDTypography>
                          <TextField
                            size="small"
                            value={t.value ?? ""}
                            onChange={(e) => updateThreshold(i, ti, e.target.value)}
                            sx={{ maxWidth: 170 }}
                          />
                        </Box>
                      ))
                    )}
                  </Box>
                ))}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 0.5,
                    mt: 1,
                    p: 1,
                    bgcolor: "#faf7ff",
                    borderRadius: 1,
                  }}
                >
                  <Checkbox
                    size="small"
                    checked={accepted}
                    onChange={(e) => setAccepted(e.target.checked)}
                    sx={{ p: 0.25, color: "#52009b", "&.Mui-checked": { color: "#52009b" } }}
                  />
                  <MDTypography variant="caption" sx={{ color: "#52009b" }}>
                    I accept responsibility for monitoring these signposts and acting on their
                    alerts.
                  </MDTypography>
                </Box>
              </Box>
            )}
            {!aiLoading && error && (
              <MDTypography variant="caption" sx={{ color: "#b71c1c", display: "block", mt: 1 }}>
                {error}
              </MDTypography>
            )}
            {!aiLoading && (
              <Box sx={{ mt: 1.5 }}>
                <MDButton
                  variant="text"
                  sx={{ color: "#52009b" }}
                  onClick={() => {
                    if (reviewMode) setReviewMode(false);
                    else setAiMode(false);
                    setError("");
                  }}
                >
                  {reviewMode && reviewSource === "ai"
                    ? "← Back to suggestions"
                    : "← Back to library results"}
                </MDButton>
              </Box>
            )}
          </>
        )}
        {!requested && !aiMode && !reviewMode && (
          <>
            {/* Assistant reply */}
            <Box sx={{ mb: 2, minHeight: 24 }}>
              {loading ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CircularProgress size={18} sx={{ color: "#52009b" }} />
                  <MDTypography variant="caption" color="text.secondary">
                    Searching {isWeb ? "the web" : "our library"} using your risk &amp; company
                    context…
                  </MDTypography>
                </Box>
              ) : (
                <MDTypography variant="body2">{result?.reply}</MDTypography>
              )}
            </Box>

            {/* Suggestions */}
            {!loading &&
              suggestions.map((s, i) => (
                <Box
                  key={s.libraryId || s.sourceUrl || i}
                  onClick={!isWeb ? () => toggleSelect(s) : undefined}
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1,
                    border: selected[s.libraryId]
                      ? "1px solid #52009b"
                      : "1px solid rgba(82,0,155,0.15)",
                    bgcolor: selected[s.libraryId] ? "rgba(82,0,155,0.06)" : "transparent",
                    borderRadius: 1,
                    p: 1.5,
                    mb: 1,
                    cursor: !isWeb ? "pointer" : "default",
                  }}
                >
                  {!isWeb && (
                    <Checkbox
                      size="small"
                      checked={!!selected[s.libraryId]}
                      onChange={() => toggleSelect(s)}
                      onClick={(e) => e.stopPropagation()}
                      sx={{ p: 0.5, color: "#52009b", "&.Mui-checked": { color: "#52009b" } }}
                    />
                  )}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                      <MDTypography variant="button" fontWeight="bold">
                        {s.name}
                      </MDTypography>
                      <Chip
                        size="small"
                        label={`${s.confidence}% match`}
                        sx={{
                          bgcolor: confBg(s.confidence),
                          color: confColor(s.confidence),
                          fontWeight: 700,
                        }}
                      />
                    </Box>
                    {s.description && (
                      <MDTypography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "block" }}
                      >
                        {s.description}
                      </MDTypography>
                    )}
                    {s.sourceUrl && (
                      <Link
                        href={
                          /^https?:\/\//.test(s.sourceUrl) ? s.sourceUrl : `https://${s.sourceUrl}`
                        }
                        target="_blank"
                        rel="noopener"
                        sx={{ fontSize: "0.72rem" }}
                      >
                        {s.sourceUrl}
                      </Link>
                    )}
                  </Box>
                  {isWeb ? (
                    <MDButton
                      size="small"
                      variant="contained"
                      sx={{
                        bgcolor: "#52009b",
                        color: "#fff",
                        flexShrink: 0,
                        "&:hover": { bgcolor: "#3a0070" },
                      }}
                      disabled={requestingUrl === (s.sourceUrl || s.name)}
                      onClick={() => requestSource(s)}
                    >
                      {requestingUrl === (s.sourceUrl || s.name) ? "Requesting…" : "Request"}
                    </MDButton>
                  ) : (
                    <MDButton
                      size="small"
                      variant="contained"
                      sx={{ bgcolor: "#52009b", color: "#fff", "&:hover": { bgcolor: "#3a0070" } }}
                      onClick={(e) => {
                        e.stopPropagation();
                        startLibraryReview([s]);
                      }}
                    >
                      Set up
                    </MDButton>
                  )}
                </Box>
              ))}

            {/* Error surface */}
            {!loading && error && (
              <MDTypography variant="caption" sx={{ color: "#b71c1c", display: "block", mt: 1 }}>
                {error}
              </MDTypography>
            )}

            {/* Escalation: library first, then let AI propose brand-new signposts, then the web. */}
            {!loading && !isWeb && (
              <Box sx={{ mt: 1.5, pt: 1.5, borderTop: "1px dashed rgba(82,0,155,0.2)" }}>
                <MDTypography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mb: 0.5 }}
                >
                  {suggestions.length
                    ? "Not what you need? Have AI propose new signposts, or search the web:"
                    : "No suitable signposts in our library — have AI propose new ones, or search the web:"}
                </MDTypography>
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  <MDButton
                    variant="contained"
                    sx={{ bgcolor: "#52009b", color: "#fff", "&:hover": { bgcolor: "#3a0070" } }}
                    onClick={runSuggest}
                  >
                    ✨ Suggest new with AI
                  </MDButton>
                  <MDButton
                    variant="outlined"
                    sx={{ color: "#52009b", borderColor: "#52009b" }}
                    onClick={() => runFind("web")}
                  >
                    🔍 Search external sources
                  </MDButton>
                </Box>
              </Box>
            )}
            {/* When viewing web results, allow going back to the library results. */}
            {!loading && isWeb && (
              <Box sx={{ mt: 1.5 }}>
                <MDButton
                  variant="text"
                  sx={{ color: "#52009b" }}
                  onClick={() => runFind("internal")}
                >
                  ← Back to library results
                </MDButton>
              </Box>
            )}

            {/* Refine via chat */}
            {!loading && (
              <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
                <TextField
                  size="small"
                  fullWidth
                  placeholder="Refine the search (e.g. focus on jet fuel price indices)…"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && input.trim()) {
                      runFind(result?.mode || "internal", input.trim());
                      setInput("");
                    }
                  }}
                />
                <MDButton
                  variant="outlined"
                  disabled={!input.trim()}
                  onClick={() => {
                    runFind(result?.mode || "internal", input.trim());
                    setInput("");
                  }}
                >
                  Send
                </MDButton>
              </Box>
            )}

            {/* Feedback */}
            {!loading && suggestions.length > 0 && (
              <Box sx={{ mt: 2, display: "flex", alignItems: "center", gap: 1 }}>
                <MDTypography variant="caption" color="text.secondary">
                  Were these results useful?
                </MDTypography>
                {feedbackDone ? (
                  <MDTypography variant="caption" sx={{ color: "#1b5e20" }}>
                    Thanks for the feedback!
                  </MDTypography>
                ) : (
                  ["good", "indifferent", "bad"].map((r) => (
                    <Chip
                      key={r}
                      size="small"
                      label={FEEDBACK_LABELS[r]}
                      onClick={() => sendFeedback(r)}
                      sx={{ cursor: "pointer" }}
                    />
                  ))
                )}
              </Box>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <MDButton onClick={onClose} variant="text" sx={{ color: "#666" }}>
          Close
        </MDButton>
        {reviewMode && (
          <MDButton
            variant="contained"
            sx={{ bgcolor: "#52009b", color: "#fff", "&:hover": { bgcolor: "#3a0070" } }}
            disabled={creating || !accepted}
            onClick={confirmCreate}
          >
            {creating ? "Setting up…" : "Set up signposts"}
          </MDButton>
        )}
        {aiMode && !reviewMode && !aiLoading && aiData && !aiData.error && (
          <MDButton
            variant="contained"
            sx={{ bgcolor: "#52009b", color: "#fff", "&:hover": { bgcolor: "#3a0070" } }}
            disabled={aiPickedCount === 0}
            onClick={startReview}
          >
            {`Review & set up (${aiPickedCount})`}
          </MDButton>
        )}
        {!aiMode && !isWeb && !reviewMode && (
          <MDButton
            variant="contained"
            sx={{ bgcolor: "#52009b", color: "#fff", "&:hover": { bgcolor: "#3a0070" } }}
            disabled={Object.keys(selected).length === 0}
            onClick={() => startLibraryReview()}
          >
            {`Review & set up (${Object.keys(selected).length})`}
          </MDButton>
        )}
      </DialogActions>
    </Dialog>
  );
}
